import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PopoverProps {
  /** Content of the trigger button, typically a small icon. */
  trigger: ReactNode;
  /** Accessible name of the trigger button and the panel. */
  label: string;
  /**
   * Panel content. The panel is a flex column with a capped height — give the part that
   * should scroll `min-h-0 flex-1 overflow-y-auto` to keep header and footer fixed.
   */
  children: ReactNode;
  className?: string;
}

/** Same overlay shadow as Modal, SlideOver and SearchSelect (design guidelines). */
const OVERLAY_SHADOW = 'shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]';

const GAP = 6;
const VIEWPORT_MARGIN = 12;
const PANEL_WIDTH = 320;
const PANEL_MAX_HEIGHT = 384;
/** Below the trigger unless less than this fits there and above has more room. */
const MIN_HEIGHT_BELOW = 200;

interface Placement {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
}

function computePlacement(rect: DOMRect): Placement {
  // why: clientWidth/-Height exclude the page scrollbar — innerWidth would let the panel
  // reach into it.
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const width = Math.min(PANEL_WIDTH, viewportWidth - 2 * VIEWPORT_MARGIN);
  const left = Math.min(
    Math.max(rect.left, VIEWPORT_MARGIN),
    viewportWidth - width - VIEWPORT_MARGIN,
  );
  const spaceBelow = viewportHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;

  if (spaceBelow < MIN_HEIGHT_BELOW && spaceAbove > spaceBelow) {
    return {
      left,
      bottom: viewportHeight - rect.top + GAP,
      width,
      maxHeight: Math.min(PANEL_MAX_HEIGHT, spaceAbove),
    };
  }
  return { left, top: rect.bottom + GAP, width, maxHeight: Math.min(PANEL_MAX_HEIGHT, spaceBelow) };
}

/**
 * Click popover for richer content than a `Tooltip` (lists, amounts). Opens below the
 * trigger and flips above when there is not enough room; its height is capped to the
 * viewport, so long content scrolls inside the panel. Closes on Escape, outside click,
 * a second click, resize and page scroll. Rendered into `document.body` so overflow
 * containers cannot clip it.
 */
export function Popover({ trigger, label, children, className = '' }: PopoverProps) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const close = useCallback(() => setPlacement(null), []);

  const toggle = () => {
    if (placement) {
      close();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPlacement(computePlacement(rect));
  };

  useEffect(() => {
    if (!placement) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    };
    // why: the panel is `position: fixed` and would not follow the page — close on page
    // scroll, but keep it open while the list inside the panel scrolls.
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [placement, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={placement !== null}
        aria-controls={placement ? panelId : undefined}
        className={`inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A871]/40 focus-visible:ring-offset-1 ${className}`}
      >
        {trigger}
      </button>
      {placement &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={label}
            // Above the Modal layer (z-[80] in Modal.tsx); coordinates are measured at runtime.
            style={{
              left: placement.left,
              top: placement.top,
              bottom: placement.bottom,
              maxHeight: placement.maxHeight,
              width: placement.width,
            }}
            className={`fixed z-[90] flex flex-col rounded-md border border-slate-200 bg-white text-[12px] leading-snug text-slate-700 ${OVERLAY_SHADOW}`}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

Popover.displayName = 'Popover';
